import { defineComponent, h, ref } from 'vue'

import { bsComponentProps, useBsComponent } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'
import { domHandlers } from '../quasarHandlers'

/**
 * Text truncated to `limit`, with a (...) toggle to see the rest. The toggle only appears when there is
 * actually more to show.
 *
 * `html` renders the text as markup instead of escaping it — used for descriptions authors have formatted.
 */
export default defineComponent({
  name: 'BsSnippet',

  /**
   * Declared so `input` is an emitted event rather than a fallthrough attribute: undeclared, the
   * `onInput` the render attaches stays in `$attrs` and Vue applies it to the root element too, so a
   * single DOM input event ran the handler twice.
   */
  emits: ['input'],

  props: {
    ...bsComponentProps,
    limit: { type: Number, default: void 0 },
    text: { type: String, default: void 0 },
    html: { type: Boolean, default: false }
  },

  setup (props, { emit }) {
    const bs = useBsComponent({ vm: useVm(), props, emit })

    const showing = ref(false)

    return { ...bs, showing }
  },

  methods: {
    /** Renamed from `__renderContent`: Vue does not proxy setup or method keys beginning with `_`. */
    renderContent () {
      if (!this.text) {
        return h('span', '')
      }

      const text = this.text.substring(0, this.showing ? Number.MAX_SAFE_INTEGER : this.limit)
      /**
       * Either innerHTML or a text child, never both keys.
       *
       * This used to declare `innerHTML: undefined` in plain-text mode as well, and Vue 2's dom-props
       * module cleared the children whenever that key was present -- so expanding a plain-text
       * snippet threw the new text away and left the truncated version on screen. Vue 3 warns about
       * the pairing instead of silently dropping the children, which is what surfaced it.
       */
      const nodes = [this.html
        ? h('span', { innerHTML: text })
        : h('span', [text])]

      if (this.text.length > this.limit) {
        nodes.push(h('span', {
          class: 'text-secondary text-bold text-underline cursor-pointer on-right-xs',
          onClick: () => {
              this.showing = !this.showing
            }
        }, [this.showing === false ? '(...)' : '(^--)']))
      }

      return nodes
    }
  },

  render () {
    return h('div', {
      class: ['bs-text-caption', this.getClasses()],
      ...domHandlers(this.getListeners())
    }, [
      this.renderContent()
    ])
  }
})
